import { floatifyColor, type Pixel, type RgbaColor, type RgbChannel, type UsedRgbChannel } from "../utils";
import multichanGradientColoring from "../shaders/multichancoloring/multichangradientcoloring.fs.glsl";

const DECODE_PRECISION = 7;

function reduceToHalf(colormap: ColorStop[]): ColorStop[] {
  return colormap.filter((_el, i) => i === 0 || i % 2 || i === colormap.length - 1);
}

function reduceToUnder(colormap: ColorStop[], maxSize = 50): ColorStop[] {
  if (colormap.length <= maxSize) {
    return colormap;
  }

  const reduced = reduceToHalf(colormap);
  return reduceToUnder(reduced, maxSize);
}

function generateGlslCodeForStops(
  stops: ColorStop[],
  smooth: boolean,
  options: { inputName: string; outputName: string } = {
    inputName: "v",
    outputName: "c",
  },
) {
  const reducedColormap = reduceToUnder(stops);

  let code = "";

  for (let i = 0; i < reducedColormap.length; i++) {
    const stop = reducedColormap[i];

    if (i === 0) {
      code += `vec4 ${options.outputName}=${floatifyColor(stop.color)};\n`;
    }

    const _v = stop.value;
    const _c = floatifyColor(stop.color);

    if (smooth && i + 1 < reducedColormap.length) {
      const _v2 = reducedColormap[i + 1].value;
      const _c2 = floatifyColor(reducedColormap[i + 1].color);
      const rampValue =
        Number.parseFloat((_v - _v2).toFixed(DECODE_PRECISION)) === 0
          ? "0."
          : `(${options.inputName} - ${_v.toFixed(DECODE_PRECISION)}) / (${(_v2 - _v).toFixed(DECODE_PRECISION)})`;
      code += `${i > 0 ? " else " : ""} if(${options.inputName}>=${_v.toFixed(
        DECODE_PRECISION,
      )}&&${options.inputName}<${_v2.toFixed(DECODE_PRECISION)}) { ${
        options.outputName
      }=mix(${_c},${_c2}, ${rampValue} ); }\n`;
    } else {
      code += `if(${options.inputName}>=${_v.toFixed(DECODE_PRECISION)})${options.outputName}=${_c};\n`;
    }
  }

  return code;
}

/**
 *
 */
export class ColoringFragmentBase {
  /**
   * @param code Inputs: `vec4 dst`, `vec4 data`. Outputs into `dst`.
   */
  constructor(
    private readonly code: string,
    private readonly decode: DecoderOptions,
  ) {
    this.decode.min = this.decode.min ?? 0;
    this.decode.max = this.decode.max ?? 1;
  }

  /**
   * Get the value in the unit interval [0, 1] computed with `.decode.min` and `.decode.max`
   * @param value
   * @returns
   */
  codeValue(value: number): number {
    return (value - this.decode.min) / (this.decode.max - this.decode.min);
  }

  /**
   * Given a value in the unit interval [0, 1] get the real world value bounded by `.decode.min` and `.decode.max`
   * @param value
   * @returns
   */
  decodeValue(value: number): number {
    return this.decode.min + value * (this.decode.max - this.decode.min);
  }

  /**
   * Get real world values of the given pixel (with rgb or rgba in [0, 1]) for the channel(s)
   * specified in `.decode.channel`
   * @param pixel
   * @returns
   */
  decodeChannel(pixel: Pixel): number | number[] {
    if (this.decode.channel.length > 1) {
      const a = this.decodeValue(pixel[<RgbChannel>this.decode.channel[0]]);
      const b = this.decodeValue(pixel[<RgbChannel>this.decode.channel[1]]);
      return [a, b, Math.sqrt(a * a + b * b)];
    }

    return this.decodeValue(pixel[<RgbChannel>this.decode.channel]);
  }

  /**
   * Generate shader/GLSL code for blending
   * @param name
   * @returns
   */
  getBlendCode(name: string): string {
    const blendCode = `${this.code} d.rgb=(d.rgb*d.a*(1.-c.a)+c.rgb*c.a)/(d.a*(1.-c.a)+c.a); d.a=d.a+c.a*(1.-d.a);`;

    return `vec4 ${name}(vec4 d,vec4 data){${blendCode}return d;}`;
  }

  /**
   * Generate shader/GLSL code
   * @param name
   * @returns
   */
  getCode(name: string): string {
    return `vec4 ${name}(vec4 data){${this.code}return c;}`;
  }
}

/**
 * Describes how to read the data value from the input files.
 */
export interface DecoderOptions {
  /**
   * Channel to be used to read the value from - `"r"`/`"g"`/`"b"`.
   * If more letters are present (`"rg"`), each channel is decoded separately and
   * then the vector length is calculated.
   *
   * Irrelevant when decoding data for `VolumeLayer`.
   */
  channel: UsedRgbChannel;
  /**
   * Minimum value encoded in each channel. (Value of `0` in the raster = this value.)
   */
  min: number;
  /**
   * Maximum value encoded in each channel.
   */
  max: number;
}

/**
 * Generate shader/GLSL code for decoding
 * @param param0
 * @returns
 */
function generateDecodeCode({ channel = "r", min = 0, max = 1 }: DecoderOptions) {
  let code = `${min.toFixed(DECODE_PRECISION)}+data.${channel}*${(max - min).toFixed(DECODE_PRECISION)}`;

  if (channel.length > 1) {
    code = `length(${code})`;
  }

  return `float v=${code};`;
}

/**
 * ColorStop
 */
export interface ColorStop {
  /**
   * The "value" at which this ColorStop should be applied.
   */
  value: number;
  /**
   * RGB[A] - Array of 3-4 numbers. 0-255 per channel.
   */
  color: RgbaColor;
}

/**
 * GradientColoringFragmentOptions
 */
export type GradientColoringFragmentOptions = {
  decode: DecoderOptions;
  /**
   * The color stops of the gradient.
   */
  stops?: ColorStop[];
  /**
   * Apply the gradient smoothly, rather then discrete steps.
   */
  smooth?: boolean;
  /**
   * 0-1
   */
  opacity?: number;
};

/**
 * Use color gradient to generate coloring from the pixel values.
 */
export class GradientColoringFragment extends ColoringFragmentBase {
  /**
   */
  constructor(options: GradientColoringFragmentOptions) {
    const decode = options.decode;
    const stops = options.stops || [
      { value: decode.min, color: [0, 0, 0, 0] },
      { value: decode.max, color: [255, 255, 255, 255] },
    ];
    const smooth = options.smooth ?? true;
    const opacity = options.opacity ?? 1;

    let code = generateDecodeCode(decode);
    code += generateGlslCodeForStops(stops, smooth);
    code += `c.a*=${opacity.toFixed(3)};`;

    super(code, decode);
  }
}

/**
 * OpacityColoringFragmentOptions
 */
export interface OpacityColoringFragmentOptions {
  decode: DecoderOptions;
  /**
   * RGB[A] - Array of 3-4 numbers. 0-255 per channel.
   */
  color?: RgbaColor;
  /**
   * 0-1
   */
  opacity?: number;
}

/**
 * Use a single color, only change opacity based on the decoded value.
 */
export class OpacityColoringFragment extends ColoringFragmentBase {
  /**
   * .
   */
  constructor(options: OpacityColoringFragmentOptions) {
    const decode = options.decode;
    const color = options.color || [255, 255, 255];
    const opacity = options.opacity ?? 1;
    const code = `${generateDecodeCode(decode)} vec4 c=${floatifyColor(color)}; c.a*=v*${opacity.toFixed(3)};`;

    super(code, decode);
  }
}

export interface MultiChannelDecoderOptions {
  /**
   * In the polynomial f(x) = ax^2 + bx + c
   * polynomialCoefDegree2 is "a"
   */
  polynomialCoefDegree2: number;

  /**
   * In the polynomial f(x) = ax^2 + bx + c
   * polynomialCoefDegree2 is "b"
   */
  polynomialCoefDegree1: number;

  /**
   * In the polynomial f(x) = ax^2 + bx + c
   * polynomialCoefDegree2 is "c"
   */
  polynomialConstant: number;
}

/**
 * The color stops as to be used by MultiChannelGradientColoringFragment
 */
export type StopsPerCategoryType = {
  /**
   * A category. Categories are the values stored in the alpha channel (in [0, 255]).
   * If the category value is `"all"` then the color stops will apply to all categories
   * and if there are other StopsPerCategory for specific category number, they will be ignored.
   */
  category?: number | "all";

  /**
   * The color stops are defining the colormap to be used.
   */
  stops: Array<ColorStop>;
};

/**
 * Options as per howto interpret image channels in the context of a multi channel gradient coloring fragment
 */
export type MultiChannelGradientColoringFragmentOptions = {
  /**
   * Polynomial information as per how to decode the multi channel value.
   */
  decode: MultiChannelDecoderOptions;
  /**
   * The color stops for each gradient. There is a gradient per category.
   */
  stopsPerCategory: Array<StopsPerCategoryType>;
  /**
   * Apply the gradient smoothly, rather then discrete steps.
   */
  smooth?: boolean;
  /**
   * 0-1
   */
  opacity?: number;

  /**
   * Channel to use for a category.
   * Can be one of "r", "g", "b" or "a"
   * (default: "a")
   */
  categoryChannel?: "r" | "g" | "b" | "a";

  /**
   * Channel(s) used to apply the polynomial coeficients (see MultiChannelDecoderOptions)
   */
  valueChannels?: "rgb" | "rg" | "r" | "gb" | "g" | "b";
};

export class MultiChannelGradientColoringFragment {
  // The default polynomial is f(x) = 0x^2 + 1x + 0, or put simply: f(x) = x ,
  // so no transformation is applied to the data.
  private decode: MultiChannelDecoderOptions = {
    polynomialCoefDegree2: 0,
    polynomialCoefDegree1: 1,
    polynomialConstant: 0,
  };

  // The default arrays of multiple colorstops contains a gradient definition for only one category,
  // groing from black to white on the data range [0, 255]
  private stopsPerCategory: Array<StopsPerCategoryType> = [
    {
      category: "all",
      stops: [
        { value: 0, color: [0, 0, 0] },
        { value: 255, color: [255, 255, 255] },
      ],
    },
  ];

  private smooth: boolean;
  private opacity: number;
  private code: string;
  private categoryChannel: "r" | "g" | "b" | "a";
  private valueChannels: "rgb" | "rg" | "r" | "gb" | "g" | "b";
  private valueNumberOfChannels: number;

  constructor(options: MultiChannelGradientColoringFragmentOptions) {
    this.decode = options.decode;
    this.stopsPerCategory = options.stopsPerCategory;
    this.smooth = options.smooth ?? true;
    this.opacity = options.opacity ?? 1;
    this.categoryChannel = options.categoryChannel ?? "a";
    this.valueChannels = options.valueChannels ?? "rgb";
    this.valueNumberOfChannels = this.valueChannels.length;

    const catchAllStops = this.stopsPerCategory.filter((stopsForCategory) => {
      if (stopsForCategory.category !== "all" && typeof stopsForCategory.category !== "number") {
        throw new Error("The category can only be a number or the string 'all'.");
      }

      return stopsForCategory.category === "all";
    });

    let stopsPerCategoryFiltered = this.stopsPerCategory;

    if (catchAllStops.length) {
      stopsPerCategoryFiltered = catchAllStops;
    }

    const gradientFunctionDefinitions = stopsPerCategoryFiltered
      .map((stopsForCategory) => {
        let functionCode = `vec4 _mccf_${stopsForCategory.category}(float v) {\n`;
        functionCode += `${generateGlslCodeForStops(stopsForCategory.stops, this.smooth)}`;
        functionCode += "return c;}\n";
        return functionCode;
      })
      .join("\n");

    let getColorCode = "vec4 getColor(int category, float v) {\n";
    getColorCode += "vec4 outputColor;\n";

    // if one category is "*", we use it for all the the values, regardless of the category. It prevails the other
    // categories and behaves as a "catch all".
    if (catchAllStops.length) {
      getColorCode += "outputColor  = _mccf_all(v);\n";
    } else {
      getColorCode += this.stopsPerCategory
        .map(
          (stopsForCategory, i) =>
            `${i === 0 ? "" : "else "}if (category == ${
              stopsForCategory.category
            }) outputColor  = _mccf_${stopsForCategory.category}(v);`,
        )
        .join("\n");
      getColorCode += "\n else outputColor = vec4(0., 0., 0., 0.);\n";
    }

    getColorCode += `outputColor.a *= ${this.opacity.toFixed(3)};\n`; // hardcoding some opacity
    getColorCode += "return outputColor;}\n";

    this.code = multichanGradientColoring
      .replace("<gradient_function_definitions>", gradientFunctionDefinitions)
      .replace("<getColor_code>", getColorCode)
      .replace("<polynomialCoefDegree2>", this.decode.polynomialCoefDegree2.toFixed(DECODE_PRECISION))
      .replace("<polynomialCoefDegree1>", this.decode.polynomialCoefDegree1.toFixed(DECODE_PRECISION))
      .replace("<polynomialConstant>", this.decode.polynomialConstant.toFixed(DECODE_PRECISION))
      .replace("<uses_categories>", catchAllStops.length ? "0" : "1")
      .replace("<value_number_channels>", this.valueNumberOfChannels.toFixed())
      .replaceAll("<category_channel>", this.categoryChannel)
      .replaceAll("<value_channels>", this.valueChannels);
  }

  getCode(): string {
    return this.code;
  }

  decodeChannel_ORIG(pixel: Pixel): number[] {
    const inter255 = {
      r: pixel.r * 255,
      g: pixel.g * 255,
      b: pixel.b * 255,
      a: pixel.a * 255,
    };

    // x is in the range of [0, 16777215]
    const x = inter255.r * 256 * 256 + inter255.g * 256 + inter255.b;
    const y =
      this.decode.polynomialCoefDegree2 * x * x +
      this.decode.polynomialCoefDegree1 * x +
      this.decode.polynomialConstant;

    return [y, inter255.a];
  }

  decodeChannel(pixel: Pixel): number[] {
    const pixel255 = {
      r: pixel.r * 255,
      g: pixel.g * 255,
      b: pixel.b * 255,
      a: pixel.a * 255,
    };
    const valueChannels = this.valueChannels.split("") as Array<"r" | "g" | "b" | "a">;
    let x = 0;

    if (this.valueNumberOfChannels === 1) {
      x = pixel255[valueChannels[0]];
    } else if (this.valueNumberOfChannels === 2) {
      x = pixel255[valueChannels[0]] * 256 + pixel255[valueChannels[1]];
    } else if (this.valueNumberOfChannels === 3) {
      x = pixel255[valueChannels[0]] * 256 * 256 + pixel255[valueChannels[1]] * 256 + pixel255[valueChannels[2]];
    }

    const y =
      this.decode.polynomialCoefDegree2 * x * x +
      this.decode.polynomialCoefDegree1 * x +
      this.decode.polynomialConstant;

    return [y, pixel255[this.categoryChannel]];
  }
}
